import { useEffect, useRef, useCallback, useState } from 'react';
import { PT, SPOT_DIVISOR, TRACKED_SYMBOLS, SYMBOL_NAMES } from '../lib/constants';

const HEARTBEAT_INTERVAL = 9000;
const REFRESH_INTERVAL = 30000;
const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 15000;
const STALE_TIMEOUT = 30000; // No message in 30s = stale

/**
 * Manages the cTrader WebSocket connection lifecycle:
 * connect → authenticate → subscribe → handle messages → reconnect.
 *
 * Returns: { status, trader, positions, prices }
 */
export default function useCTraderSocket(config) {
  const [status, setStatus] = useState('loading');
  const [trader, setTrader] = useState(null);
  const [positions, setPositions] = useState([]);
  const [prices, setPrices] = useState({});

  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const refreshRef = useRef(null);
  const msgIdRef = useRef(0);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const destroyedRef = useRef(false);
  const connectedOnceRef = useRef(false);
  const lastMessageRef = useRef(0);
  const staleCheckRef = useRef(null);
  const pricesRef = useRef({});

  const send = useCallback((payloadType, payload = {}, clientMsgId) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({
      payloadType,
      payload,
      clientMsgId: clientMsgId || `m_${++msgIdRef.current}`,
    }));
  }, []);

  const cleanupConnection = useCallback(() => {
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
    if (refreshRef.current) { clearInterval(refreshRef.current); refreshRef.current = null; }
    if (staleCheckRef.current) { clearInterval(staleCheckRef.current); staleCheckRef.current = null; }
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    }
  }, []);

  useEffect(() => {
    if (!config?.clientId || !config?.accessToken) {
      setStatus('no-config');
      return;
    }

    destroyedRef.current = false;

    const connect = () => {
      if (destroyedRef.current) return;
      setStatus('connecting');

      const host = config.isDemo ? 'demo.ctraderapi.com' : 'live.ctraderapi.com';
      const ws = new WebSocket(`wss://${host}:5036`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
        lastMessageRef.current = Date.now();
        // Start heartbeat
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ payloadType: PT.HEARTBEAT }));
          }
        }, HEARTBEAT_INTERVAL);
        // Stale connection detection — force reconnect if no messages received
        staleCheckRef.current = setInterval(() => {
          if (Date.now() - lastMessageRef.current > STALE_TIMEOUT && ws.readyState === WebSocket.OPEN) {
            console.warn('[cTrader] Connection stale, forcing reconnect');
            ws.close();
          }
        }, STALE_TIMEOUT);
        // Authenticate app
        send(PT.APP_AUTH_REQ, {
          clientId: config.clientId,
          clientSecret: config.clientSecret,
        }, 'app_auth');
      };

      ws.onmessage = (e) => {
        lastMessageRef.current = Date.now();
        try {
          const msg = JSON.parse(e.data);
          handleMessage(msg);
        } catch (err) { console.warn('[cTrader parse error]', err.message); }
      };

      ws.onclose = () => {
        if (destroyedRef.current) return;
        setStatus('disconnected');
        cleanupConnection();
        // Only auto-reconnect if we connected successfully at least once
        if (connectedOnceRef.current) {
          const delay = reconnectDelayRef.current;
          reconnectDelayRef.current = Math.min(delay * 1.5, MAX_RECONNECT_DELAY);
          setTimeout(connect, delay);
        }
      };

      ws.onerror = (e) => { console.error('[cTrader WS error]', e); setStatus('error'); };
    };

    const loadInitialData = () => {
      const aid = config.accountId;
      send(PT.TRADER_REQ, { ctidTraderAccountId: aid });
      send(PT.RECONCILE_REQ, { ctidTraderAccountId: aid, returnProtectionOrders: true });

      for (const symbolId of Object.values(TRACKED_SYMBOLS)) {
        send(PT.SUBSCRIBE_SPOTS_REQ, {
          ctidTraderAccountId: aid,
          symbolId,
          subscribeToSpotTimestamp: true,
        });
      }

      refreshRef.current = setInterval(() => {
        send(PT.TRADER_REQ, { ctidTraderAccountId: aid });
        send(PT.RECONCILE_REQ, { ctidTraderAccountId: aid, returnProtectionOrders: true });
      }, REFRESH_INTERVAL);
    };

    const handleMessage = (msg) => {
      const pt = msg.payloadType;
      const p = msg.payload || {};
      const id = msg.clientMsgId;

      if (id === 'app_auth' && pt === PT.APP_AUTH_RES) {
        send(PT.ACCOUNT_AUTH_REQ, {
          ctidTraderAccountId: config.accountId,
          accessToken: config.accessToken,
        }, 'acct_auth');
        return;
      }

      if (id === 'acct_auth' && pt === PT.ACCOUNT_AUTH_RES) {
        connectedOnceRef.current = true;
        setStatus('connected');
        loadInitialData();
        return;
      }

      switch (pt) {
        case PT.TRADER_RES: {
          const t = p.trader;
          if (!t) return;
          const d = Math.pow(10, t.moneyDigits || 2);
          setTrader({
            balance: t.balance / d,
            moneyDigits: t.moneyDigits || 2,
            leverageInCents: t.leverageInCents,
          });
          break;
        }
        case PT.RECONCILE_RES: {
          const mapped = (p.position || [])
            .filter(pos => pos.positionStatus === 1)
            .map(pos => ({
              id: pos.positionId,
              symbolId: pos.tradeData.symbolId,
              symbol: SYMBOL_NAMES[pos.tradeData.symbolId] || `SYM_${pos.tradeData.symbolId}`,
              side: pos.tradeData.tradeSide === 1 ? 'buy' : 'sell',
              volume: pos.tradeData.volume,
              entryPrice: pos.price,
              stopLoss: pos.stopLoss,
              takeProfit: pos.takeProfit,
              usedMargin: pos.usedMargin,
              moneyDigits: pos.moneyDigits || 2,
              openTime: pos.tradeData.openTimestamp,
            }));
          setPositions(mapped);

          // Subscribe to spot prices for any new position symbols
          const subscribedIds = new Set(Object.values(TRACKED_SYMBOLS));
          for (const pos of mapped) {
            if (!subscribedIds.has(pos.symbolId)) {
              subscribedIds.add(pos.symbolId);
              send(PT.SUBSCRIBE_SPOTS_REQ, {
                ctidTraderAccountId: config.accountId,
                symbolId: pos.symbolId,
                subscribeToSpotTimestamp: true,
              });
            }
          }
          break;
        }
        case PT.SPOT_EVENT: {
          const prev = pricesRef.current[p.symbolId] || {};
          const updated = {
            bid: p.bid != null ? p.bid / SPOT_DIVISOR : prev.bid || 0,
            ask: p.ask != null ? p.ask / SPOT_DIVISOR : prev.ask || 0,
            timestamp: p.timestamp,
          };
          pricesRef.current = { ...pricesRef.current, [p.symbolId]: updated };
          setPrices({ ...pricesRef.current });
          break;
        }
        case PT.EXECUTION_EVENT: {
          // Position closed/filled — refresh positions
          send(PT.TRADER_REQ, { ctidTraderAccountId: config.accountId });
          send(PT.RECONCILE_REQ, { ctidTraderAccountId: config.accountId, returnProtectionOrders: true });
          break;
        }
        case PT.ERROR_RES:
          console.error('[cTrader]', p.errorCode, p.description);
          break;
      }
    };

    connect();

    // Reconnect when tab becomes visible again (only if we connected before)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && connectedOnceRef.current && !destroyedRef.current) {
        const ws = wsRef.current;
        if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
          reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      destroyedRef.current = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cleanupConnection();
    };
  }, [config, send, cleanupConnection]);

  const closePosition = useCallback((positionId, volume) => {
    send(PT.CLOSE_POSITION_REQ, {
      ctidTraderAccountId: config?.accountId,
      positionId: parseInt(positionId),
      ...(volume != null && { volume }),
    }, `close_${positionId}`);
  }, [send, config]);

  return { status, trader, positions, prices, closePosition };
}
